import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  ArrowLeft, AlertTriangle, Shield, CheckCircle, XCircle,
  ChevronLeft, ChevronRight, Clock, Eye, ArrowUpCircle
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminFraudFlags() {
  const [flags, setFlags] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [actionFlag, setActionFlag] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');

  const headers = useCallback(() => {
    const token = document.cookie.split('; ').find(r => r.startsWith('session_token='))?.split('=')[1];
    return { 'Content-Type': 'application/json', ...(token ? { 'Authorization': `Bearer ${token}` } : {}) };
  }, []);

  const loadFlags = useCallback(async (p = 1) => {
    setLoading(true);
    try {
      const res = await fetch(`${API}/affiliate/admin/fraud-flags?page=${p}&limit=20`, { headers: headers() });
      if (res.ok) {
        const d = await res.json();
        setFlags(d.flags || []);
        setTotal(d.total || 0);
        setPage(d.page || 1);
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [headers]);

  useEffect(() => { loadFlags(); }, [loadFlags]);

  const updateFlag = async (flagId, status) => {
    try {
      const res = await fetch(`${API}/affiliate/admin/fraud-flags/${flagId}`, {
        method: 'PATCH', headers: headers(),
        body: JSON.stringify({ status, admin_notes: adminNotes }),
      });
      if (res.ok) {
        toast.success(`Flag ${status}`);
        setActionFlag(null);
        setAdminNotes('');
        loadFlags(page);
      } else {
        toast.error('Failed to update flag');
      }
    } catch { toast.error('Error updating flag'); }
  };

  const severityBadge = (severity) => {
    const map = { high: 'bg-red-50 text-red-700', medium: 'bg-amber-50 text-amber-700', low: 'bg-gray-50 text-gray-600' };
    return <Badge className={`text-xs capitalize ${map[severity] || 'bg-gray-50 text-gray-600'}`}>{severity}</Badge>;
  };

  const statusBadge = (status) => {
    const map = { open: 'bg-red-50 text-red-700', cleared: 'bg-green-50 text-green-700', escalated: 'bg-orange-50 text-orange-700', dismissed: 'bg-gray-50 text-gray-500' };
    return <Badge className={`text-xs capitalize ${map[status] || 'bg-gray-50 text-gray-600'}`}>{status}</Badge>;
  };

  const totalPages = Math.ceil(total / 20);
  const openCount = flags.filter(f => f.status === 'open').length;

  return (
    <div className="max-w-6xl mx-auto" data-testid="fraud-flags-page">
      <div className="flex items-center gap-4 mb-8">
        <Link to="/admin"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Admin</Button></Link>
        <div>
          <h1 className="text-xl font-bold text-[#0f1f33]">Fraud Flags</h1>
          <p className="text-sm text-gray-400">Review, resolve, or dismiss suspicious activity flags.</p>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-red-50 flex items-center justify-center"><AlertTriangle className="w-3.5 h-3.5 text-red-600" /></div>
            <span className="text-xs text-gray-400">Total Flags</span>
          </div>
          <p className="text-lg font-bold text-[#0f1f33]">{total}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-amber-50 flex items-center justify-center"><Clock className="w-3.5 h-3.5 text-amber-600" /></div>
            <span className="text-xs text-gray-400">Open</span>
          </div>
          <p className="text-lg font-bold text-[#0f1f33]">{openCount}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-green-50 flex items-center justify-center"><CheckCircle className="w-3.5 h-3.5 text-green-600" /></div>
            <span className="text-xs text-gray-400">Cleared</span>
          </div>
          <p className="text-lg font-bold text-[#0f1f33]">{flags.filter(f => f.status === 'cleared').length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-100 p-4 shadow-sm">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-7 h-7 rounded-lg bg-orange-50 flex items-center justify-center"><ArrowUpCircle className="w-3.5 h-3.5 text-orange-600" /></div>
            <span className="text-xs text-gray-400">Escalated</span>
          </div>
          <p className="text-lg font-bold text-[#0f1f33]">{flags.filter(f => f.status === 'escalated').length}</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <div key={i} className="h-24 bg-gray-50 rounded-xl animate-pulse border border-gray-100" />)}</div>
      ) : flags.length === 0 ? (
        <Card className="border-gray-100 shadow-sm">
          <CardContent className="p-12 text-center">
            <Shield className="w-12 h-12 text-green-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-[#0f1f33] mb-2">No fraud flags</h3>
            <p className="text-sm text-gray-400">The system hasn't detected any suspicious activity.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {flags.map((f, i) => (
            <Card key={i} className={`shadow-sm transition-all ${f.status === 'open' ? 'border-red-200 bg-red-50/20' : 'border-gray-100'}`}>
              <CardContent className="p-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className={`w-4 h-4 ${f.severity === 'high' ? 'text-red-500' : f.severity === 'medium' ? 'text-amber-500' : 'text-gray-400'}`} />
                      <p className="font-semibold text-[#0f1f33]">{f.reason}</p>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400 mb-2">
                      {f.partner_name && <span>Partner: <strong className="text-gray-600">{f.partner_name}</strong></span>}
                      {f.partner_email && <span>({f.partner_email})</span>}
                      <span>{f.created_at?.slice(0, 10)}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      {severityBadge(f.severity)}
                      {statusBadge(f.status)}
                    </div>
                    {f.admin_notes && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg">
                        <p className="text-xs text-gray-500"><strong>Admin Notes:</strong> {f.admin_notes}</p>
                      </div>
                    )}
                  </div>
                  {f.status === 'open' && (
                    <div className="flex flex-col gap-2">
                      {actionFlag === f.flag_id ? (
                        <div className="w-64 space-y-2">
                          <Textarea value={adminNotes} onChange={e => setAdminNotes(e.target.value)} placeholder="Admin notes (optional)..." className="text-sm min-h-[60px]" data-testid={`flag-notes-${f.flag_id}`} />
                          <div className="flex gap-1">
                            <Button size="sm" className="bg-green-600 hover:bg-green-700 text-white text-xs flex-1" onClick={() => updateFlag(f.flag_id, 'cleared')} data-testid={`clear-flag-${f.flag_id}`}>Clear</Button>
                            <Button size="sm" className="bg-orange-600 hover:bg-orange-700 text-white text-xs flex-1" onClick={() => updateFlag(f.flag_id, 'escalated')}>Escalate</Button>
                            <Button size="sm" variant="ghost" className="text-xs" onClick={() => updateFlag(f.flag_id, 'dismissed')}>Dismiss</Button>
                          </div>
                          <Button size="sm" variant="ghost" className="w-full text-xs text-gray-400" onClick={() => { setActionFlag(null); setAdminNotes(''); }}>Cancel</Button>
                        </div>
                      ) : (
                        <Button size="sm" variant="outline" onClick={() => setActionFlag(f.flag_id)} data-testid={`review-flag-${f.flag_id}`}>
                          <Eye className="w-3.5 h-3.5 mr-1" /> Review
                        </Button>
                      )}
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-3 pt-4">
              <Button variant="ghost" size="sm" disabled={page <= 1} onClick={() => loadFlags(page - 1)}><ChevronLeft className="w-4 h-4" /></Button>
              <span className="text-sm text-gray-500">Page {page} of {totalPages}</span>
              <Button variant="ghost" size="sm" disabled={page >= totalPages} onClick={() => loadFlags(page + 1)}><ChevronRight className="w-4 h-4" /></Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
